import React from 'react';
import GeneralMenu from '../../navigation/GeneralMenu';
import GeneralChart from '@/components/seo/GeneralChart';
import {
  isEmpty,
  isNoEmptyArray,
  filterArrayWithValidItems,
  isObjWithValidEntries,
} from '@/utils/seo-content';

interface Props {
  show: boolean;
  companyName: string;
  whatToDoData: Record<string, any>;
  partnerData: Record<string, any>;
  competitionData: Record<string, any>;
  latestNewsData: Record<string, any>;
}

const PANORAMA_ITEMS = [
  { name: '这家公司是做什么的？', id: 'company-panorama_what-to-do' },
  {
    name: '公司有哪些重要的客户和合作伙伴？',
    id: 'company-panorama_important-clients-and-partners',
  },
  { name: '在市场中面临怎样的竞争', id: 'company-panorama_market-competition' },
  {
    name: '公司最新动态信息整理',
    id: 'company-panorama_dynamic-information-organization',
  },
];

const PanoramaTabPanel = (props: Props) => {
  const {
    show,
    whatToDoData = {},
    partnerData = {},
    competitionData = {},
    latestNewsData = {},
    companyName,
  } = props;

  const menuList = [PANORAMA_ITEMS[0]];
  if (!isEmpty(partnerData['tip'])) {
    menuList.push(PANORAMA_ITEMS[1]);
  }
  if (!isEmpty(competitionData['tip'])) {
    menuList.push(PANORAMA_ITEMS[2]);
  }
  if (!isEmpty(latestNewsData['tip'])) {
    menuList.push(PANORAMA_ITEMS[3]);
  }

  const businessPerformance = filterArrayWithValidItems(
    whatToDoData['经营规模与业务表现'],
  );
  const businessCoverage = filterArrayWithValidItems(
    whatToDoData['全球与行业布局'],
  );
  const featuresAndDifferences = filterArrayWithValidItems(
    competitionData['竞品对照说明'],
  );
  const strategicPartners = filterArrayWithValidItems(
    partnerData['战略级客户'],
  );
  const overseasPartners = filterArrayWithValidItems(
    partnerData['海外与生态合作伙伴'],
  );
  const emergingCustomers = filterArrayWithValidItems(
    partnerData['新兴客户与潜力赛道'],
  );
  const majorCompetitors = filterArrayWithValidItems(
    competitionData['主要竞争对手'],
  );
  const coreBusiness = filterArrayWithValidItems(whatToDoData['核心业务构成']);

  return (
    <div hidden={!show} className="relative">
      <GeneralMenu menuList={menuList} />
      <h2 className="text-xl font-semibold">作为求职者，如何评估这家公司</h2>
      <GeneralChart
        id={PANORAMA_ITEMS[0].id}
        title={PANORAMA_ITEMS[0].name}
        tip={whatToDoData['tip'] || ''}
        tipBgColor="bg-web-purple-100"
      >
        <h4 className="font-semibold mb-2">公司介绍</h4>
        <p>{whatToDoData['公司定位与本质'] || ''}</p>
        {businessPerformance.length > 0 && (
          <>
            <h4 className="font-semibold mt-4 mb-2">经营概况</h4>
            <ul className="list-inside !ml-2">
              {businessPerformance.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {coreBusiness.length > 0 && (
          <>
            <h4 className="font-semibold mt-4 mb-2">核心业务与产品</h4>
            <ul className="list-inside !ml-2">
              {coreBusiness.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {businessCoverage.length > 0 && (
          <>
            <h4 className="font-semibold mt-4 mb-2">业务覆盖</h4>
            <ul className="list-inside !ml-2">
              {businessCoverage.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {whatToDoData['竞争优势与壁垒'] && (
          <>
            <h4 className="font-semibold mt-4 mb-2">公司荣誉</h4>
            <p>{whatToDoData['竞争优势与壁垒']}</p>
          </>
        )}
      </GeneralChart>
      <GeneralChart
        id={PANORAMA_ITEMS[1].id}
        title={PANORAMA_ITEMS[1].name}
        tip={partnerData['tip']}
        tipBgColor="bg-web-purple-100"
      >
        {strategicPartners.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">战略级客户</h4>
            <ul className="list-inside !ml-2">
              {strategicPartners.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {isObjWithValidEntries(partnerData['重点行业客户']) && (
          <>
            <h4 className="font-semibold mb-2 mt-4">重点行业客户</h4>
            <ul className="list-inside !ml-2">
              {(Object.keys(partnerData['重点行业客户']) || []).map(
                (key: string, index: number) => (
                  <li
                    key={index}
                  >{`${key}：${partnerData['重点行业客户'][key][0]}`}</li>
                ),
              )}
            </ul>
          </>
        )}
        {overseasPartners.length > 0 && (
          <>
            <h4 className="font-semibold mb-2 mt-4">海外战略合作伙伴</h4>
            <ul className="list-inside !ml-2">
              {overseasPartners.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
        {emergingCustomers.length > 0 && (
          <>
            <h4 className="font-semibold mb-2 mt-4">新兴产业客户</h4>
            <ul className="list-inside !ml-2">
              {emergingCustomers.map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </>
        )}
      </GeneralChart>
      {!isEmpty(competitionData['tip']) && (
        <GeneralChart
          id={PANORAMA_ITEMS[2].id}
          title={PANORAMA_ITEMS[2].name}
          tip={competitionData['tip']}
          tipBgColor="bg-web-purple-100"
        >
          {majorCompetitors.length > 0 && (
            <>
              <h4 className="font-semibold mt-4 mb-2">
                主要竞争对手（业务重合度80%以上）
              </h4>
              <ul className="list-inside !ml-2">
                {majorCompetitors.map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </>
          )}
          {featuresAndDifferences.length > 0 && (
            <>
              <h4 className="font-semibold mt-4 mb-2">特点与差异</h4>
              <ul className="list-inside !ml-2">
                {featuresAndDifferences.map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </>
          )}
          <h4 className="font-semibold mt-4 mb-2">{companyName}的优势</h4>
          <p>{competitionData['公司的优势位置'] || ''}</p>
        </GeneralChart>
      )}
      {!isEmpty(latestNewsData['tip']) && (
        <GeneralChart
          id={PANORAMA_ITEMS[3].id}
          title={PANORAMA_ITEMS[3].name}
          tip={latestNewsData['tip']}
          tipBgColor="bg-web-purple-100"
        >
          {isNoEmptyArray(latestNewsData['近期关键动态']) && (
            <>
              <h4 className="font-semibold mt-4 mb-2">近期关键动态</h4>
              <ul className="list-inside !ml-2">
                {(latestNewsData['近期关键动态'] || []).map(
                  (item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ),
                )}
              </ul>
            </>
          )}
          <h4 className="font-semibold mt-4 mb-2">综合前景判断</h4>
          <ul className="list-inside !ml-2">
            {(latestNewsData['综合前景判断']?.['要点'] || []).map(
              (item: string, index: number) => (
                <li key={index}>{item}</li>
              ),
            )}
          </ul>
          <h4 className="font-semibold mt-4 mb-2">谨慎点</h4>
          <ul className="list-inside !ml-2">
            {(latestNewsData['遗憾点或风险观察'] || []).map(
              (item: string, index: number) => (
                <li key={index}>{item}</li>
              ),
            )}
          </ul>
        </GeneralChart>
      )}
    </div>
  );
};

export default PanoramaTabPanel;
